import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import AutomationsList from './comps/AutomationsList';
import LinearFlowView from './comps/LinearFlowView';
import callAPI from '../../comps/fetch/main';
import { fireDB } from '../../funcs/firebase';
import { collection, doc, setDoc, serverTimestamp, getDocs, getDoc, deleteDoc } from 'firebase/firestore';
import './main.css';
import { v4 as uuidv4 } from 'uuid';
import { Modal, Drawer } from 'antd';
import FlowInsights from './comps/FlowInsights';
import Sider from '../../comps/sider/main';

export default function QuickDM({ embedded = false }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const [messageApi, contextHolder] = message.useMessage();

    // State for automations list
    const [automations, setAutomations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    // State for selected automation (when editing)
    const [selectedAutomation, setSelectedAutomation] = useState(null);
    const [editMode, setEditMode] = useState(false);

    // Insights Modal State
    const [insightsModalOpen, setInsightsModalOpen] = useState(false);
    const [insightsData, setInsightsData] = useState([]);
    const [insightsLoading, setInsightsLoading] = useState(false);
    const [currentInsightsFlowName, setCurrentInsightsFlowName] = useState('');

    // Fetch automations list
    useEffect(() => {
        fetchAutomations();
    }, []);

    // If ID is provided, load that automation
    useEffect(() => {
        if (!embedded && id && id !== 'new') {
            loadAutomation(id);
        } else if (!embedded && id === 'new') {
            setSelectedAutomation(createNewAutomation());
            setEditMode(true);
        }
    }, [id, embedded]);

    const fetchAutomations = async () => {
        setLoading(true);
        let draftFlows = [];
        try {
            const user_detail = JSON.parse(localStorage.getItem("userData"));
            if (user_detail?.user_id) {
                const draftCollectionRef = collection(fireDB, "automations", user_detail.user_id, "draft_automations");
                const snapshot = await getDocs(draftCollectionRef);
                draftFlows = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            }
        } catch (e) { console.error("Error fetching drafts:", e); }

        callAPI({
            url: '/flow_editor',
            method: 'GET',
            loadingState: () => { }, // Handled manually
            dataState: (data) => {
                let apiFlows = [];
                if (data?.flows) {
                    apiFlows = data.flows;
                }
                // Merge: Drafts overwrite API if same ID (assuming draft is newer)
                const flowMap = new Map();
                apiFlows.forEach(f => flowMap.set(f.id, f));
                draftFlows.forEach(f => flowMap.set(f.id, f));

                // Filter for Quick DM flows only
                const allFlows = Array.from(flowMap.values());
                const quickDmFlows = allFlows.filter(f => f.quick_dm === true);

                setAutomations(quickDmFlows);
                setLoading(false);
            }
        });
    };

    const loadAutomation = async (automationId) => {
        setLoading(true);
        try {
            const user_detail = JSON.parse(localStorage.getItem("userData"));
            if (user_detail?.user_id) {
                const docRef = doc(fireDB, "automations", user_detail.user_id, "draft_automations", automationId);
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    // If saved with cleanAutomationData (Source Steps), unwrap it to top level
                    // so LinearFlowView can read 'steps' and 'trigger' correctly.
                    const finalData = data.cleanAutomationData ? { ...data, ...data.cleanAutomationData } : data;
                    setSelectedAutomation({ id: docSnap.id, ...finalData });
                    setEditMode(true);
                } else {
                    messageApi.error("Automation not found");
                    if (!embedded) navigate('/quick-dm');
                    else setEditMode(false);
                }
            }
        } catch (e) {
            console.error("Error loading draft:", e);
            messageApi.error("Error loading automation");
        } finally {
            setLoading(false);
        }
    };

    const createNewAutomation = () => ({
        id: `quick-dm-${uuidv4()}`,
        name: 'New Quick DM',
        live: false,
        trigger: {
            type: 'dm_reply',
            keywords: [],
            automate_all: false
        },
        steps: [],
        confirmation: {
            text: 'Thanks! We\'ll get back to you soon.'
        }
    });

    const handleCreateNew = () => {
        if (embedded) {
            setSelectedAutomation(createNewAutomation());
            setEditMode(true);
        } else {
            navigate('/quick-dm/new');
        }
    };

    const handleSelectAutomation = (automation) => {
        if (embedded) {
            loadAutomation(automation.id);
        } else {
            navigate(`/quick-dm/${automation.id}`);
        }
    };

    const handleBack = () => {
        setEditMode(false);
        setSelectedAutomation(null);
        if (!embedded) navigate('/quick-dm');
    };

    const handleSave = async (automationData, isPublishing = false) => {
        console.log("Saving Automation Data (Quick DM):", automationData);

        // Convert Quick DM format to Flow Editor payload
        const flowPayload = convertToFlowPayload(automationData);
        flowPayload.quick_dm = true;
        console.log("Converted Flow Payload:", flowPayload);

        // Save as draft to Firestore directly
        if (!isPublishing) setLoading(true);
        try {
            const user_detail = JSON.parse(localStorage.getItem("userData"));
            if (!user_detail?.user_id) throw new Error("User not found");

            const draftRef = doc(fireDB, "automations", user_detail.user_id, "draft_automations", automationData.id);
            // Sanitize data using custom helper to filter undefined values
            const cleanAutomationData = removeUndefined(automationData);

            // Save the RAW automation data (Source of Truth) so LinearFlowView can edit it later
            await setDoc(draftRef, {
                ...flowPayload,
                quick_dm: true,
                cleanAutomationData,
                updated_at: serverTimestamp(),
                live: automationData.live || false
            });

            if (!isPublishing) {
                messageApi.success('Draft saved successfully!');
                fetchAutomations();
            }
            return true;
        } catch (error) {
            console.error("Error saving draft:", error);
            messageApi.error("Failed to save draft");
            return false;
        } finally {
            if (!isPublishing) setLoading(false);
        }
    };

    const handlePublish = async (automationData) => {
        setLoading(true);
        // 1. First Save Draft
        const saved = await handleSave(automationData, true);
        if (!saved) {
            setLoading(false);
            return;
        }

        // 2. Call API to Publish
        const flowPayload = convertToFlowPayload(automationData);
        flowPayload.quick_dm = true;

        // Prepare clean source data for backend storage (so Discard works)
        const cleanAutomationData = removeUndefined(automationData);

        callAPI({
            url: `/flow_editor?flow_id=${automationData.id}`,
            method: 'POST',
            body: {
                ...flowPayload,
                live: true,
                cleanAutomationData // <--- Include Source Steps in Live Payload
            },
            loadingState: (loading) => { if (!loading) setLoading(false) },
            dataState: async (res) => {
                if (res?.code === 200 || res?.code === "200") {

                    // Update Firestore to reflect Live status
                    try {
                        const user_detail = JSON.parse(localStorage.getItem("userData"));
                        if (user_detail?.user_id) {
                            const draftRef = doc(fireDB, "automations", user_detail.user_id, "draft_automations", automationData.id);
                            await setDoc(draftRef, { live: true }, { merge: true });
                        }
                    } catch (e) {
                        console.error("Error updating live status in Firestore:", e);
                    }

                    messageApi.success('Automation published & live!');
                    fetchAutomations();
                    // Update current selected automation to show active state
                    setSelectedAutomation(prev => ({ ...prev, live: true }));
                    handleBack();
                } else {
                    messageApi.error('Failed to publish automation');
                }
            }
        });
    };

    const handleDiscard = async () => {
        const automationId = selectedAutomation?.id;
        if (!automationId) return;

        setLoading(true);
        // 1. Fetch Live Version from API
        callAPI({
            url: `/flow_editor?flow_id=${automationId}`,
            method: 'GET',
            loadingState: () => { },
            dataState: async (data) => {
                if (data && data?.data?.cleanAutomationData) {
                    // Note: Reverting to live loses "cleanAutomationData" if API doesn't return it.
                    // But if API stores payload as is, it might not have cleanAutomationData.
                    // Assuming user accepts losing draft changes.
                    // However, if we overwrite draft with API data, we need to ensure LinearFlowView likes it.
                    // If API data lacks Steps, we are back to square one?
                    // FIX: Backend usually validates Schema. 
                    // If User WANTS "Discard Draft", they expect previous SAVED state?
                    // Or previous LIVE state?
                    // "make it live which sends to backend... discard draft which again loads from backend".

                    // Assuming Backend returns 'cleanAutomationData' if it was part of saved payload?
                    // No, legacy backend might strip it?
                    // IF backend preserves unknown fields, good.
                    // IF NOT, then 'Discard' might break editing if API version is legacy.

                    // BUT for this specific task: overwrite Draft with fetched data.
                    try {
                        const user_detail = JSON.parse(localStorage.getItem("userData"));
                        if (user_detail?.user_id) {
                            const draftRef = doc(fireDB, "automations", user_detail.user_id, "draft_automations", automationId);
                            await setDoc(draftRef, { ...data?.data, updated_at: serverTimestamp() });
                            messageApi.success("Draft discarded. Reloading live version.");
                            loadAutomation(automationId);
                        }
                    } catch (e) {
                        console.error("Error discarding:", e);
                        messageApi.error("Failed to discard draft");
                        setLoading(false);
                    }
                } else {
                    messageApi.warning("No live version found to revert to.");
                    setLoading(false);
                }
            }
        });
    };

    const handleToggle = (automationId, isLive) => {
        if (!isLive) {
            // Deactivate: Delete from backend, set live=false in Firestore
            callAPI({
                url: `/flow_editor?flow_id=${automationId}`,
                method: 'DELETE',
                loadingState: () => { },
                dataState: async (res) => {
                    if (res?.code === 200 || res?.code === "200") {
                        try {
                            const user_detail = JSON.parse(localStorage.getItem("userData"));
                            if (user_detail?.user_id) {
                                const draftRef = doc(fireDB, "automations", user_detail.user_id, "draft_automations", automationId);
                                await setDoc(draftRef, { live: false }, { merge: true });
                            }
                        } catch (e) {
                            console.error("Error updating live status in Firestore:", e);
                        }
                        messageApi.success('Automation deactivated (Draft Mode)');
                        fetchAutomations();
                    } else {
                        messageApi.error('Failed to deactivate automation');
                    }
                }
            });
        } else {
            // Activate - Blocked from List View
            messageApi.info("Please go inside the configuration to activate it.");
        }
    };

    const handleDelete = async (automationId) => {
        setLoading(true);
        try {
            // 1. Delete from Firestore Drafts
            const user_detail = JSON.parse(localStorage.getItem("userData"));
            if (user_detail?.user_id) {
                const draftRef = doc(fireDB, "automations", user_detail.user_id, "draft_automations", automationId);
                await deleteDoc(draftRef);
            }
        } catch (e) {
            console.error("Error deleting draft:", e);
        }

        // 2. Delete from Backend API
        callAPI({
            url: `/flow_editor?flow_id=${automationId}`,
            method: 'DELETE',
            loadingState: setLoading,
            dataState: (res) => {
                // If success or even if not found (already deleted in backend but was stuck in draft), refresh
                if (res?.code === 200 || res?.code === "200") {
                    messageApi.success('Automation deleted');
                } else {
                    // Even if backend fails (maybe didn't exist), we deleted the draft so it's effectively gone from UI
                    messageApi.success('Automation deleted');
                }
                fetchAutomations();
            }
        });
    };

    const handleViewInsights = (automation) => {
        setInsightsModalOpen(true);
        setCurrentInsightsFlowName(automation.name);
        setInsightsLoading(true);

        callAPI({
            url: `/flow_editor_response?flow_id=${automation.id}`,
            method: 'GET',
            loadingState: setInsightsLoading,
            dataState: (res) => {
                if (res?.data) {
                    setInsightsData(res.data);
                } else {
                    setInsightsData([]);
                }
            }
        });
    };

    const filteredAutomations = automations.filter(auto => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        const name = (auto.name || '').toLowerCase();
        const keywords = (auto.trigger?.keywords || []).join(' ').toLowerCase();
        const isAnyMessage = auto.trigger?.automate_all === true || auto.nodes?.[0]?.data?.auto_reply?.dm_reply?.automate_all === 'true';

        return name.includes(query) || keywords.includes(query) || (isAnyMessage && 'any message'.includes(query));
    });

    return (
        <div className={!embedded ? "page-layout" : ""}>
            {contextHolder}

            {!embedded && <Sider />}
            <div className={!embedded ? "page-content dashboard-page" : ""}>
                <div className={!embedded ? "" : ""}>
                    {(!editMode || embedded) && (
                        <AutomationsList
                            automations={filteredAutomations}
                            loading={loading}
                            searchQuery={searchQuery}
                            onSearchChange={setSearchQuery}
                            onCreateNew={handleCreateNew}
                            onSelect={handleSelectAutomation}
                            onToggle={handleToggle}
                            onDelete={handleDelete}
                            onViewInsights={handleViewInsights}
                        />
                    )}

                    {/* Editor View - Conditional rendering based on mode */}
                    {embedded ? (
                        <Drawer
                            title={selectedAutomation?.name || "Edit Automation"}
                            placement="right"
                            width="100%"
                            onClose={handleBack}
                            open={editMode}
                            bodyStyle={{ padding: 0 }}
                            destroyOnClose
                        >
                            <LinearFlowView
                                automation={selectedAutomation}
                                onBack={handleBack}
                                onSave={handleSave}
                                onPublish={handlePublish}
                                onDiscard={handleDiscard}
                                loading={loading}
                                automations={automations}
                            />
                        </Drawer>
                    ) : (
                        editMode && (
                            <LinearFlowView
                                automation={selectedAutomation}
                                onBack={handleBack}
                                onSave={handleSave}
                                onPublish={handlePublish}
                                onDiscard={handleDiscard}
                                loading={loading}
                                automations={automations}
                            />
                        )
                    )}
                </div>

                {/* Insights Modal */}
                <Modal
                    title={`Insights: ${currentInsightsFlowName}`}
                    open={insightsModalOpen}
                    onCancel={() => setInsightsModalOpen(false)}
                    width={800}
                    footer={null}
                    className="insights-modal"
                >
                    <FlowInsights
                        data={insightsData}
                        loading={insightsLoading}
                        automationName={currentInsightsFlowName}
                    />
                </Modal>
            </div>
        </div>
    );
}

// Convert Quick DM state to Flow Editor payload
function convertToFlowPayload(quickDM) {
    const nodes = [];
    const edges = [];
    let yPosition = 50;
    const X_POSITION = 50;
    const Y_SPACING = 150;

    // 1. Create Trigger Node
    const triggerNode = {
        id: 'node-1',
        type: 'triggerComp',
        position: { x: X_POSITION, y: yPosition },
        data: {
            auto_reply: {
                dm_reply: {
                    enabled: quickDM.trigger?.type === 'dm_reply',
                    automate_all: quickDM.trigger?.automate_all ? 'true' : 'false',
                    one_time_only: quickDM.trigger?.one_time_only !== false, // Default true
                    triger_words: quickDM.trigger?.keywords || []
                },
                story_reply: {
                    enabled: false,
                    automate_all: 'false',
                    triger_words: [],
                    all_stories: 'false'
                },
                comment_reply: {
                    enabled: false,
                    automate_all: 'false',
                    triger_words: [],
                    all_posts: 'false'
                },
                welcome_message: quickDM.welcome_message || null
            }
        }
    };
    nodes.push(triggerNode);
    let previousNodeId = 'node-1';

    // 2. Create nodes for each step
    quickDM.steps?.forEach((step, index) => {
        yPosition += Y_SPACING;
        const nodeId = `step-${index + 1}`;

        if (step.type === 'message') {
            nodes.push({
                id: nodeId,
                type: 'messageReply',
                position: { x: X_POSITION, y: yPosition },
                data: {
                    messages: [{
                        id: `msg-${index}`,
                        type: step.messageType || 'text',
                        text: step.text || '',
                        image_url: step.image_url || '',
                        templates: step.templates || [],
                        smartReplies: step.smartReplies || []
                    }]
                }
            });
        } else if (step.type === 'question') {
            nodes.push({
                id: nodeId,
                type: 'questionNode',
                position: { x: X_POSITION, y: yPosition },
                data: {
                    messages: [{
                        id: `q-${index}`,
                        question_type: step.questionType || 'text',
                        question: step.question || '',
                        skip_option: step.skip_option || { enable: false },
                        retry_attempt: step.retry_attempt || { count: 3 },
                        wrong_input: step.wrong_input || { message: '' },
                        custom_variable_name: step.variable || ''
                    }]
                }
            });
        } else if (step.type === 'delay') {
            // Add delay as a message within previous node or create new
            nodes.push({
                id: nodeId,
                type: 'messageReply',
                position: { x: X_POSITION, y: yPosition },
                data: {
                    messages: [{
                        id: `delay-${index}`,
                        type: 'delay',
                        delayDuration: step.duration || 3,
                        durationType: step.durationType || 's'
                    }]
                }
            });
        } else if (step.type === 'tag') {
            nodes.push({
                id: nodeId,
                type: 'tagContact',
                position: { x: X_POSITION, y: yPosition },
                data: {
                    tags: step.tags || [],
                    tagsData: step.tagsData || []
                }
            });
        }

        // Create edge from previous node
        edges.push({
            id: `e-${previousNodeId}-${nodeId}`,
            source: previousNodeId,
            target: `${nodeId}`,
            targetHandle: `${nodeId}-left`,
            type: 'custom-edge',
            sourceHandle: `${previousNodeId}-right`,
            markerEnd: { type: 'arrowclosed' },
            data: {}
        });
        previousNodeId = nodeId;
    });

    // 3. Create confirmation node (Removed as per request)
    // if (quickDM.confirmation?.text) { ... }

    return {
        name: quickDM.name,
        live: quickDM.live || false,
        nodes,
        edges
    };
}

// Helper to remove undefined values for Firestore
function removeUndefined(obj) {
    if (obj === undefined) return null; // or undefined, but used in recursion
    if (obj === null || typeof obj !== 'object') return obj;

    if (Array.isArray(obj)) {
        return obj.map(v => removeUndefined(v));
    }

    const newObj = {};
    Object.keys(obj).forEach(key => {
        const val = removeUndefined(obj[key]);
        if (val !== undefined) {
            newObj[key] = val;
        }
    });
    return newObj;
}
